import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import PropTypes from 'prop-types'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { PlanActions } from '@galo-monitor/stores/plan'
import DataTable from '@galo-monitor/web-components/DataTable'
import DataTableIcons from '@galo-monitor/web-components/DataTable/icons'
import PlanModal from '@galo-monitor/web-containers/modals/company/PlanModal'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal/ErrorModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const FarmPlansScreen = ({ slug }) => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { getActionsBySlug } = useRoutes()

  const userActions = useMemo(
    () => getActionsBySlug(slug),
    [getActionsBySlug, slug]
  )

  const { data: plans, mutate, isValidating } = useGaloMonitorApi('/companies/plans')

  const columns = useMemo(
    () => [
      {
        title: t('name of {this}', { this: t('plan', { howMany: 1 }), howMany: 1, gender: 'male' }),
        field: 'name'
      },
      {
        title: t('{this} enabled', { this: t('module', { howMany: 2 }), howMany: 2, gender: 'male' }),
        field: 'modulesFarms'
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-farm',
        Component: PlanModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, plan) => {
      createDialog({
        id: 'edit-plan',
        Component: PlanModal,
        props: {
          plan,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, plan) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'male',
            howMany: 1,
            this: t('plan', { howMany: 1 }),
            name: plan.name
          }),
          onConfirm: () => {
            dispatch(PlanActions.removePlan(
              plan,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('plan', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'male', howMany: 1 }))
                }))
                mutate()
              },
              () => {
                createDialog({
                  id: 'module-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'male',
                      action: toLower(t('remove')),
                      this: toLower(t('plan', { howMany: 1 }))
                    })  }.`,
                    message: `${ t('you need detach all farms accounts to delete this plan', {
                      this: t('plan', { howMany: 1 }),
                      action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                    })  }.`
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  const actions = useMemo(
    () => {
      const datatableActions = []
      if (userActions.edit) {
        datatableActions.push(
          rowData => ({
            icon: DataTableIcons.Edit,
            tooltip: t('datagrid body edit tooltip'),
            onClick: onEditClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small',
              ...(!rowData.isEditable ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      if (userActions.delete) {
        datatableActions.push(
          rowData => ({
            icon: DataTableIcons.Delete,
            tooltip: t('datagrid body delete tooltip'),
            onClick: onDeleteClick,
            disabled: isValidating,
            iconButtonProps: {
              size: 'small',
              ...(!rowData.isEditable ? { style: { display: 'none' } } : {})
            },
            position: 'row'
          })
        )
      }

      return datatableActions
    },
    [isValidating, onEditClick, onDeleteClick, t, userActions.delete, userActions.edit]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ plans }
      columns={ columns }
      actions={ actions }
      onAddClick={ userActions.write && onAddClick }
    />
  )
}

FarmPlansScreen.propTypes = {
  slug: PropTypes.string.isRequired
}

export default FarmPlansScreen
